import type { Entitlement } from "@tdelivr/api-client/generated/tenant/tenant-entitlement.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { asDay, windowPosition, type BoardRow } from "./board.ts";

/**
 * What the plan board does to the entitlements TEN-03 returns, away from any rendering.
 *
 * **`Used` is half of every row the design draws and nothing on the platform meters it.** The grid
 * is `Entitlement · Limit · Used` and the samples are `Active drivers · 500 · 312` and
 * `API calls per month · 5,000,000 · 4,610,220`, the last badged `caution · 92% used`. TEN-03
 * publishes the limit. No operation anywhere answers how many drivers a tenant has against its
 * driver entitlement, or how many API calls it made this month — and the entitlement service
 * deliberately does not count: its responsibility is what a tenant may do, not what it did. Q-105,
 * and the decision recorded there is that **no percentage badge is drawn**, because a percentage of
 * an unknown numerator is worse than a blank.
 *
 * **The listing is deliberately unfiltered by effectiveness, so the board has to apply the window
 * itself.** An entitlement that starts next month is part of "what your business has bought", and
 * the by-capability read returns only what is in force now — so a board built on that read could
 * not have shown it at all. The cost is that `enabled: true` on a row outside its window is not a
 * claim about today, and the zones board's finding applies unchanged: a row reading `enabled` for a
 * grant that has not started tells an administrator they have a capability they do not. Both
 * correcting fields are published, so the board is simply right.
 *
 * **A null `limit` is published and the contract defines nothing for it.** `limit: input.limit ??
 * null` in TEN-03's own domain: a write that omits a limit stores a null, and no description
 * anywhere says whether that means unlimited or unrecorded. So it is drawn as the design's `—` with
 * the measured fact on it, not as "unlimited" — which would be this console deciding a quota
 * question on a page about quotas.
 */

/** The word `limit` carries, or nothing, in the design's own grouped-digit grammar. */
const grouping = new Intl.NumberFormat("en-GB", { useGrouping: true, maximumFractionDigits: 0 });

export function limitText(entitlement: Entitlement): string {
  return entitlement.limit === null ? "" : grouping.format(entitlement.limit);
}

/**
 * The state class and the word.
 *
 * `enabled` is TEN-03's only flag and it is not the whole state, for the reason `zones.ts` records
 * about `lifecycle`: a grant stored enabled and outside its effective window is not in force. The
 * four readings are the contract's own facts and the classes are the design's — `done` for a grant
 * in force, `caution` (the class whose label is "Qualified") for one whose window has not opened or
 * has closed, `closed` for one switched off.
 *
 * None of the design's own three words is reachable: `within limit` and `92% used` both need the
 * consumption Q-105 records as absent, and the design has no word for a grant outside its window.
 */
export function stateOf(
  entitlement: Entitlement,
  now: number,
): { classKey: StateClassKey; label: string; title?: string } {
  if (!entitlement.enabled) {
    return {
      classKey: "closed",
      label: "not enabled",
      title: "TEN-03 holds this capability as disabled for this tenant.",
    };
  }
  const position = windowPosition(entitlement.effectiveFrom, entitlement.effectiveUntil, now);
  if (position === "not_yet") {
    return {
      classKey: "caution",
      label: "not yet effective",
      title: `Enabled, and effective from ${entitlement.effectiveFrom}. The by-capability read — which is what a service asks before allowing something — returns only what is in force now, so this grant is not yet usable.`,
    };
  }
  if (position === "ended") {
    return {
      classKey: "caution",
      label: "no longer effective",
      title: `Enabled, and effective until ${entitlement.effectiveUntil ?? "an instant now past"}. The by-capability read returns only what is in force now, so this grant is no longer usable.`,
    };
  }
  return { classKey: "done", label: "enabled" };
}

/** The design's `Effective` grammar for a window, which is a date or a date and an end. */
export function windowText(entitlement: Entitlement): string {
  const from = asDay(entitlement.effectiveFrom);
  return entitlement.effectiveUntil === null
    ? from
    : `${from} – ${asDay(entitlement.effectiveUntil)}`;
}

/** One row of the plan board. */
export function entitlementRow(entitlement: Entitlement, now: number): BoardRow {
  const state = stateOf(entitlement, now);
  return {
    id: entitlement.id,
    cells: [
      { text: entitlement.capability, mono: true },
      {
        text: limitText(entitlement),
        ...(entitlement.limit === null
          ? {
              absent: true,
              title:
                "TEN-03 publishes this grant with no limit. A write that omits one stores a null, and nothing in the contract says whether that means unlimited or unrecorded — so it is not drawn as either.",
            }
          : {}),
      },
      {
        text: "",
        absent: true,
        title:
          "Nothing on this platform meters a tenant's use of a capability against its entitlement. TEN-03 publishes what was bought and counts nothing. Q-105.",
      },
    ],
    state,
    /*
     * One line where the design's panel lists three, and it is the one Q-105 names: "the listing
     * answers `Entitlement` and `Limit` with each row's effective window". The window is what
     * decides whether a grant is usable and it has no column, so the badge carries the verdict and
     * this carries the dates behind it. Q-108 records the deviation.
     */
    detail: [{ label: "Effective", value: windowText(entitlement) }],
    history: [
      { label: "Created", value: asDay(entitlement.createdAt) },
      { label: "Last changed", value: asDay(entitlement.updatedAt) },
      { label: "Version", value: `v${String(entitlement.version)}` },
    ],
    glyph: "workspace_premium",
  };
}

/** The states this listing can actually answer, in the order `stateOf` decides them. */
export const ENTITLEMENT_STATES: readonly string[] = [
  "enabled",
  "not yet effective",
  "no longer effective",
  "not enabled",
];

/** Which entitlements a chosen state leaves on the board. Client-side: the listing has no filter. */
export function filterEntitlements(
  entitlements: readonly Entitlement[],
  state: string,
  now: number,
): readonly Entitlement[] {
  if (state === "") return entitlements;
  return entitlements.filter((entitlement) => stateOf(entitlement, now).label === state);
}
